import { Button } from '@almbrand/button';
import { formatPolicyPriceLabel, handleNavigation } from 'common/PolicyUtil';
import { DetailsListComponent } from 'components/2-molecules/DetailsListComponent/DetailsList';
import { HttpStatusCode } from 'constantsValues/statusCode';
import { Charged } from 'generated-api/bff/api';
import React, { useEffect } from 'react';
import { IoArrowBack, IoArrowForward } from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchPolicy } from 'store/slices/policySlice';
import CoverageComponent from '../CoverageComponent/CoverageComponent';
import styles from './PolicyDetailsComponent.module.scss';

export interface PolicyDetailsComponentProps {
	description?: string;
	productImage?: {
		url: string;
		type: string;
		properties: {
			height: number;
			width: number;
			contentType: string;
			contentId: number;
		};
	};
	mainArea?: {
		items: {
			content: {
				coveredIconIndicator: {
					url: string;
					type: string;
					properties: {
						height: number;
						width: number;
					};
				};
				title: string;
				contentType: string;
				contentId: number;
			};
		}[];
	};
	contentType?: string;
	contentId?: number;
}

const PolicyDetailsComponent: React.FC<PolicyDetailsComponentProps> = ({
	description = '',
	productImage = { url: '', type: '', properties: { height: 100, width: 100, contentType: '', contentId: 0 } },
	mainArea = { items: [] },
	contentType = '',
	contentId = 0,
}) => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	// Redux State Selectors
	const { policy, status, error } = useAppSelector((state) => state.policyDetails);
	const policies = useAppSelector((state) => state.policies.policies);

	// Utility Constants
	const policyDocumentUrl = true;

	// Parse Query Parameters
	const getQueryParams = (query: string) => Object.fromEntries(new URLSearchParams(query));
	const queryParams = getQueryParams(location.search);
	const policyId = queryParams.id;

	// Fetch Policy on Mount
	useEffect(() => {
		if (policyId) {
			dispatch(fetchPolicy({ policyNo: policyId, sourceSystem: 'CodanTIA' }));
		}
	}, [dispatch, policyId]);

	// Find Policy Index
	const policyIndex = policies?.findIndex((p) => p.policy_number === policyId);

	// Error or Loading State Handling
	if (status === HttpStatusCode.Loading) return <div>Loading policy details...</div>;
	if (status === HttpStatusCode.InternalServerError || !policy)
		return <div>Error: {error || 'Policy not found!'}</div>;

	// Helper Functions for Navigation
	const handleNextPolicy = () => {
		if (!policies || policyIndex === -1) return;
		const nextIndex = (policyIndex + 1) % policies.length; // Loop to start
		const nextPolicy = policies[nextIndex];
		if (nextPolicy) handleNavigation(nextPolicy.title, nextPolicy.policy_number, navigate);
	};

	const handlePreviousPolicy = () => {
		if (!policies || policyIndex === -1) return;
		const prevIndex = (policyIndex - 1 + policies.length) % policies.length; // Loop to end
		const prevPolicy = policies[prevIndex];
		if (prevPolicy) handleNavigation(prevPolicy.title, prevPolicy.policy_number, navigate);
	};

	// Generate Coverage Blocks
	const coverageBlocks = mainArea.items
		.filter((item) => item.content.contentType === 'CoveragesBlock')
		.map((block) => ({
			icon: (
				<img
					src={block.content.coveredIconIndicator?.url || ''}
					alt='Coverage Icon'
					style={{
						width: `${block.content.coveredIconIndicator?.properties?.width || 50}px`,
						height: `${block.content.coveredIconIndicator?.properties?.height || 50}px`,
					}}
				/>
			),
			title: block.content?.title || 'No Title',
			items: [
				{
					title: 'Ansvar',
					desc: 'Dækker, hvis du eller en af husstandens andre medlemmer gør skade på andre personer eller deres ting. ',
				},
				{
					title: 'Indbo basis',
					desc: 'Dækker skader på dine ting ved brand, kortslutning, tyveri, røveri og overfald, hærværk, færdselsuheld og havari, udstrømning af væsker, storm og voldsomt sky- og tøbrud og strømafbrydelse til køle- og dybfrostanlæg. Dine cykler er dækket mod brand og tyveri for op til 18.989 kr. Har du brug for en højere forsikringssum på dine cykler, så kan der tegnes en udvidelse som dækker op til 63.294 kr.',
				},
				{
					title: 'Retshjælp',
					desc: 'Giver dig økonomisk hjælp ved retssager. Forsikringen dækker udgifter til sagsomkostninger ved private uoverensstemmelser.',
				},
				{
					title: 'Pludselig skade',
					desc: 'Dækker pludselige skader på dine ting - f.eks. hvis du vælter en vase eller sætter dig på dine briller.',
				},
			],
		}));

	// Generate Details List Data
	const isOnceCharged = policy?.price?.charged === Charged.Once;
	const detailsList = [
		{ label: 'Policenummer', value: policy?.policy_number || '' },
		{ label: 'Kundenummer', value: policy?.customer_id || '' },
		{ label: isOnceCharged ? 'Gyldig til' : 'Forfaldsdato', value: policy?.end?.date || '' },
	];

	return (
		<div className={styles.policyDetails}>
			{/* Back Button */}
			<div
				className={styles.backButton}
				onClick={() => navigate('/forsikringer')}
			>
				<div className={styles.backIconContainer}>
					<IoArrowBack className={styles.backIcon} />
				</div>
				<span className={styles.backButtonText}>Tilbage</span>
			</div>

			{/* Policy Header */}
			<div className={styles.header}>
				<div className={styles.arrowIconContainer}>
					<IoArrowBack
						className={styles.arrowIcon}
						onClick={handlePreviousPolicy}
					/>
				</div>
				<div className={styles.policyInfo}>
					<img
						src={productImage.url}
						alt='Policy Product Icon'
						className={styles.icon}
						style={{
							width: `${productImage.properties.width}px`,
							height: `${productImage.properties.height}px`,
						}}
					/>
					<div>
						<h2>{policy.title}</h2>
					</div>
					<span className={`${styles.status} ${styles[policy.status.type.toLowerCase()]}`}>
						{policy.status.subtitle}
					</span>
				</div>
				<div className={styles.arrowIconContainer}>
					<IoArrowForward
						className={styles.arrowIcon}
						onClick={handleNextPolicy}
					/>
				</div>
			</div>

			{/* Policy Details */}
			<div className={styles.details}>
				<div className={styles.detailsContent}>
					<div className={styles.detailsRow}>
						<h4>Din pris</h4>
						<p className={styles.detailsValue}>{formatPolicyPriceLabel(policy.price)}</p>
					</div>
					<div className={styles.detailsRow}>
						<p>Selvrisko</p>
						<p className={styles.detailsValue}>2.500 kr</p>
					</div>
					<div className={styles.detailsRow}>
						<p>Maks. km/år</p>
						<p className={styles.detailsValue}>15.000 km</p>
					</div>
				</div>
			</div>

			{/* Download Policy Section */}
			{policyDocumentUrl && (
				<div className={styles.downloadPolicy}>
					<Button
						// onClick={() => handleDownloadPolicyPdf(policy.policy_number)}
						buttonType='secondary'
						size='small'
					>
						Download policy
					</Button>
				</div>
			)}

			{/* Render Coverage Components */}
			{coverageBlocks.map((block, index) => (
				<CoverageComponent
					key={index}
					title={block.title}
					icon={block.icon}
					items={block.items}
				/>
			))}

			{/* Render Details List */}
			<div className={styles.detailsList}>
				<h4 className='mb-spacing-sm'>Detaljer</h4>
				<DetailsListComponent detailsList={detailsList} />
			</div>
		</div>
	);
};

export default PolicyDetailsComponent;
